/**
 * PATH: packages/ui/src/kanban/components/label-picker/TintStrip.tsx
 * FILE_ID: UI-KANBAN-LABEL-PICKER-TINT-001
 * PURPOSE: The legacy single-token "Card colour" tile-tint strip (none + the
 *   CARD_COLOR_OPTIONS swatches), extracted verbatim from the label picker.
 * WHY: FEAT-2026-07-04-KANBAN-LABEL-PICKER-V2 grew the picker past the size
 *   budget; the tint strip is a self-contained I→O block (token in, pick out).
 * FLOW: currentTint → paint selection → onPick(token|'') → parent persists.
 * DEPENDENCIES:
 *   - ../../cardColors: CARD_COLOR_OPTIONS palette.
 * RELATED:
 *   - ../KanbanCardLabelPicker.tsx: sole host.
 */
import { Ban, Check } from 'lucide-react'

import { CARD_COLOR_OPTIONS } from '../../cardColors'

export function TintStrip({
  currentTint,
  busy,
  onPick,
}: {
  /** Normalised tint token ('' = none). */
  currentTint: string
  busy: boolean
  onPick: (token: string) => void
}) {
  return (
    <div className="border-t border-neutral-100 pt-2 dark:border-zinc-800">
      <div className="mb-1 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-zinc-400">
        Card colour
      </div>
      <div
        role="group"
        aria-label="Card colour"
        data-testid="kanban-card-color-strip"
        className="flex flex-wrap items-center gap-1"
      >
        <button
          type="button"
          disabled={busy}
          data-card-action="color"
          data-testid="kanban-card-color-none"
          aria-label="No colour"
          title="No colour"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onPick('')
          }}
          className={[
            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-neutral-400',
            'border-neutral-200 bg-white hover:border-neutral-300 disabled:opacity-50',
            'dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
            currentTint === '' ? 'ring-2 ring-neutral-400 ring-offset-1 dark:ring-offset-zinc-900' : '',
          ].join(' ')}
        >
          {currentTint === '' ? (
            <Check className="h-3 w-3" aria-hidden="true" />
          ) : (
            <Ban className="h-3 w-3" aria-hidden="true" />
          )}
        </button>
        {CARD_COLOR_OPTIONS.map((opt) => {
          const isSel = currentTint === opt.token
          return (
            <button
              key={opt.token}
              type="button"
              disabled={busy}
              data-card-action="color"
              data-testid={`kanban-card-color-${opt.token}`}
              aria-label={`Colour: ${opt.label}`}
              title={opt.label}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onPick(opt.token)
              }}
              style={{ background: opt.swatch }}
              className={[
                'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                'shadow-sm ring-1 ring-black/10 transition-transform hover:scale-110 disabled:opacity-50',
                'dark:ring-white/15',
                isSel ? 'ring-2 ring-offset-1 ring-neutral-700 dark:ring-white dark:ring-offset-zinc-900' : '',
              ].join(' ')}
            >
              {isSel && (
                <Check className="h-3 w-3 text-white drop-shadow" aria-hidden="true" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
